import { verifyLogin } from 'util/cache'
import { useDocTitle } from 'util/hooks'
import { useUploadLessonMutation, useGenerateQuestionsMutation, useDeleteLessonMutation, useGetClassesQuery } from 'util/rtk-api'

import { DoneButton } from 'components/blue-button'
import UploadBox from 'components/upload-box'
import { StudentTable } from 'components/tables/student-table'
import { DifficultyPills } from 'components/tables/difficulty-pills'
import { ClassContextProvider } from 'components/class-context'
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import 'css/pages/upload-lessons.css'
import { LessonProgressModal } from './lesson-progress-modal'
import { showToast } from 'components/toast'
import { isValidUploadExtension } from 'util/upload-validation'
import { studentsMissingLevel } from 'util/student-levels'
import { MissingLevelModal } from 'components/missing-level-modal'
import { deriveGradeBand } from 'util/grade-band'
import { generateIdempotencyKey } from 'util/idempotency-key'
import { UPLOAD_LESSON_EXTENSIONS } from '@all-for-one/shared-types'

const VALID_SUPPLEMENTARY_TYPES = [
	'application/pdf',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'text/plain'
];

// Extensions, not MIME types, are what actually gate an upload -- a file's
// browser-reported MIME type comes from the OS's file-type registration,
// which is unreliable for files that passed through Google Drive, email, or
// an older export tool, and several of the accepted types below (.key
// especially) have no consistent MIME registration across OSes at all. The
// HTML accept attribute happily takes extensions directly, so the same list
// also gates the native file-picker dialog -- no second MIME-type list to
// drift against it. PRESENTATION_EXTENSIONS was previously its own
// `['.pdf', '.ppt', '.pptx']` literal, independently drifted from the
// backend's real allowlist (`api/src/services/lesson-upload.ts`), silently
// blocking legitimate .doc/.docx/.odp/.odt/.key uploads the server already
// accepted. Found in the 2026-09-30 full-tree audit; now a shared constant.
const PRESENTATION_EXTENSIONS = UPLOAD_LESSON_EXTENSIONS;
// Dead in practice -- the only caller that would pass isPresentation=false
// is the "Supplementary Documents" UploadBox below, which is commented out
// ("Removed supplementary documents support for MVP"). Kept only because
// handleFileUpload's branch still references it; not touched by this fix.
const SUPPLEMENTARY_EXTENSIONS = [...PRESENTATION_EXTENSIONS, '.txt'];

/* How long the "Ready to go" state stays up before the redirect. Long
   enough to read one short line, short enough not to feel like a stall. */
const READY_DWELL_MS = 1200;

export const UploadLessons = () => {
	const [presentationFile, setPresentationFile] = useState(null);
	const [lessonId, setLessonId] = useState(null);
	// Idempotency key (L1), same pattern as create-class-overlay.tsx's
	// requestId: generated fresh per uploaded lesson (not per component
	// lifetime -- a teacher can upload more than one lesson in one page
	// session), stable across a handleDoneClick retry for THAT lesson so a
	// client timeout can't spend a second real Gemini call. See
	// docs/AUDIT-2026-09-23-lego-rewrite.md item #10.
	const [generateQuestionsKey, setGenerateQuestionsKey] = useState<string | null>(null);
	const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
	const [gradeBand, setGradeBand] = useState<string>('3-5');
	const [showGradeBandDropdown, setShowGradeBandDropdown] = useState(false);
	const [subject, setSubject] = useState<string>('');
	const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
	const [uploadBoxKey, setUploadBoxKey] = useState(0);
	const [currentTableDifficulty, setCurrentTableDifficulty] = useState('All');
	// Generation succeeded and we are showing the confirmation before
	// redirecting. Separate from the mutation's own isGenerating, which goes
	// false the moment the request resolves.
	const [showReady, setShowReady] = useState(false);
	const readyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const navigate = useNavigate();

	// Use the RTK Query hooks
	const [uploadLesson, { isLoading: isUploading, error: uploadError }] = useUploadLessonMutation();
	const [generateQuestions, { isLoading: isGenerating }] = useGenerateQuestionsMutation();
	const [deleteLesson] = useDeleteLessonMutation();
	const { data: classesData } = useGetClassesQuery(undefined);

	const classes = [...(classesData?.classes || [])].sort((a, b) => a.name.localeCompare(b.name));
	const selectedClass = classes.find(c => c.classId === selectedClassId);
	// Blocking gate, moved here from lesson-launch (CEO, 8/22): catch a
	// missing level at the point a class gets assigned to a lesson, before
	// "Done" can be clicked -- not later when the lesson is actually launched.
	const missingLevelStudents = studentsMissingLevel(selectedClass?.students);

	// Update grade band when class is selected
	const handleClassSelect = (classId: string) => {
		setSelectedClassId(classId);
		const selected = classes.find(c => c.classId === classId);
		if (selected?.grades) {
			setGradeBand(deriveGradeBand(selected.grades));
		}
		setSubject(selected?.subject || '');
	};

	useDocTitle('All for One - Plan New Lesson');

	const handleFileUpload = async (file, isPresentation) => {
		if (!file) return;

		const validExtensions = isPresentation ? PRESENTATION_EXTENSIONS : SUPPLEMENTARY_EXTENSIONS;

		// Was validated against file.type (the browser's MIME guess) instead of
		// the extension. A real .pptx whose OS-reported type doesn't exactly
		// match the allowlist -- common for files that passed through Google
		// Drive or email -- silently failed here with no error shown, since a
		// bare `return` looks identical to "nothing selected".
		if (!isValidUploadExtension(file.name, validExtensions)) {
			showToast(`"${file.name}" isn't a supported file type. Accepted: ${validExtensions.join(', ')}`);
			return;
		}

		if (isPresentation) {
			setPresentationFile(file);
			try {
				// Use the RTK Query mutation
				const response = await uploadLesson(file).unwrap();
				if (response.lessonId) {
					setLessonId(response.lessonId);
					setGenerateQuestionsKey(generateIdempotencyKey());
				} else {
					setPresentationFile(null);
					showToast('Failed to upload lesson. Please try again.');
				}
			} catch (error) {
				// err-9: was console-only, leaving the file chip looking uploaded
				console.error('Error uploading lesson:', error);
				setPresentationFile(null);
				showToast('Failed to upload lesson. Please try again.');
			}
		} else {
			// TODO: Upload supplementary file
		}
	};

	const handleDoneClick = async () => {
		if (!selectedClassId) {
			showToast('Please select a class before generating questions.', 'info');
			return;
		}
		// Belt and suspenders alongside the blocking modal above -- don't rely
		// on the overlay alone to keep this from firing.
		if (missingLevelStudents.length > 0) {
			return;
		}
		try {
			// err-9: without .unwrap() an RTK mutation never throws, so the
			// catch below was dead code and we navigated to a questionless
			// lesson on every generation failure
			await generateQuestions({
				lessonId,
				gradeLevel: gradeBand,
				classId: selectedClassId,
				subject: subject || undefined,
				idempotencyKey: generateQuestionsKey || undefined
			}).unwrap();
			// The mockup specifies a brief "Ready to go" confirmation before the
			// redirect. Without a dwell the teacher never sees that all three
			// pathways landed -- the one moment of reassurance in this flow.
			setShowReady(true);
			readyTimerRef.current = setTimeout(
				() => navigate(`/lesson-builder?lessonId=${lessonId}`),
				READY_DWELL_MS
			);
		} catch (error) {
			console.error('Error generating questions:', error);
			showToast('Failed to generate questions. Please try again.');
		}
	};

	useEffect(() => {
		verifyLogin(navigate)
	}, [navigate])

	// Don't fire a redirect into an unmounted component if the teacher
	// navigates away during the dwell.
	useEffect(() => () => {
		if (readyTimerRef.current) clearTimeout(readyTimerRef.current)
	}, [])

	return (
		<>
			{selectedClassId && missingLevelStudents.length > 0 && (
				<MissingLevelModal classId={selectedClassId} students={missingLevelStudents} />
			)}
			<div className="upload-lessons">
				{/* mockup .page-head: eyebrow, sentence-case h1, centred rule */}
				<div className="upload-lessons-page-head">
					<span className="page-eyebrow">Start here</span>
					<div className="upload-lessons-header">
						Plan a new lesson
					</div>
					<div className="page-rule" />
				</div>
				<div className={`upload-content-row ${presentationFile ? 'has-file' : ''}`}>
					<div className="upload-box-row">
						<UploadBox
							key={uploadBoxKey}
							name="presentation documents"
							acceptedFileTypes={PRESENTATION_EXTENSIONS}
							acceptedFileExtensionsText={'.PPTX \u00b7 .PPT \u00b7 .PDF \u00b7 .DOCX \u00b7 .DOC \u00b7 .ODP \u00b7 .ODT \u00b7 .KEY'}
							onFileUpload={(file) => handleFileUpload(file, true)}
							onDismiss={async () => {
								if (lessonId) {
									try { await deleteLesson(lessonId).unwrap() } catch (e) { /* ignore */ }
								}
								setPresentationFile(null)
								setLessonId(null)
								setSelectedClassId(null)
								setUploadBoxKey(k => k + 1)
							}}
							isUploading={isUploading}
							failed={!!uploadError}
						/>
						{/* TODO: Removed supplementary documents support for MVP */}
						{/* <UploadBox
							name="Supplementary Documents"
							isActive={!!presentationFile}
							acceptedFileTypes={VALID_SUPPLEMENTARY_TYPES}
							acceptedFileExtensionsText=".PPTX, .PPT, .DOC, .DOCX, .PDF, .TXT (Max: 2)"
							maxFiles={2}
							onFileUpload={(file) => handleFileUpload(file, false)}
							optional={true}
						/> */}
					</div>
					{presentationFile && (
					<div className="class-assignment-column">
						<div className="uploaded-file-box">
							<span className="uploaded-file-name">{presentationFile.name.replace(/\.[^/.]+$/, '')}</span>
							{isUploading ? (
								<span className="uploading-indicator">Uploading...</span>
							) : (
								<button
									type="button"
									className="dismiss-file-button"
									onClick={async () => {
										if (lessonId) {
											try { await deleteLesson(lessonId).unwrap() } catch (e) { /* ignore */ }
										}
										setPresentationFile(null)
										setLessonId(null)
										setSelectedClassId(null)
										setUploadBoxKey(k => k + 1)
									}}
									aria-label="Remove file"
								>
									×
								</button>
							)}
						</div>
						{!isUploading && !uploadError && (
						<div className="class-selection-section">
							<div className="class-selection-row">
								<label htmlFor="class-select">Assign to class:</label>
								<select
									id="class-select"
									value={selectedClassId || ''}
									onChange={(e) => handleClassSelect(e.target.value)}
									className="class-dropdown"
								>
									<option value="">Select a class...</option>
									{classes.map((c) => (
										<option key={c.classId} value={c.classId}>
											{c.name}
										</option>
									))}
								</select>
							</div>
							{selectedClassId && (
								<>
									<div className="grade-band-row">
										<span>Class is {selectedClass?.grades?.length === 1 ? 'grade' : 'grades'} {selectedClass?.grades?.join(', ') || 'N/A'}. Questions will target grades </span>
										{showGradeBandDropdown ? (
											<select
												value={gradeBand}
												onChange={(e) => {
													setGradeBand(e.target.value);
													setShowGradeBandDropdown(false);
												}}
												onBlur={() => setShowGradeBandDropdown(false)}
												autoFocus
												className="grade-band-dropdown"
											>
												<option value="K-2">K-2</option>
												<option value="3-5">3-5</option>
												<option value="6-8">6-8</option>
												<option value="9-12">9-12</option>
											</select>
										) : (
											<>
												<strong>{gradeBand}</strong>
												<button
													type="button"
													onClick={() => setShowGradeBandDropdown(true)}
													className="change-grade-link"
												>
													change
												</button>
											</>
										)}
									</div>
									<div className="grade-band-row">
										<span>Class subject is </span>
										{showSubjectDropdown ? (
											<select
												value={subject}
												onChange={(e) => {
													setSubject(e.target.value);
													setShowSubjectDropdown(false);
												}}
												onBlur={() => setShowSubjectDropdown(false)}
												autoFocus
												className="grade-band-dropdown"
											>
												<option value="Science">Science</option>
												<option value="Math">Math</option>
												<option value="ELA">ELA</option>
												<option value="Social Studies">Social Studies</option>
												<option value="Other">Other</option>
											</select>
										) : (
											<>
												<strong>{subject || 'General'}</strong>
												<button
													type="button"
													onClick={() => setShowSubjectDropdown(true)}
													className="change-grade-link"
												>
													change
												</button>
											</>
										)}
									</div>
									{selectedClass && (
										<ClassContextProvider initialClass={selectedClass}>
											<div className="class-info-section">
												<DifficultyPills currentDifficulty={currentTableDifficulty} handleDifficultyChange={setCurrentTableDifficulty} />
												<div className="student-table-container">
													<StudentTable
														keyFilter={new Set(['difficulty', 'studentName', 'grade'])}
														style={{}}
														currentDifficulty={currentTableDifficulty}
														renderHeader={true}
													/>
												</div>
											</div>
										</ClassContextProvider>
									)}
								</>
							)}
							<div className="alignRight">
								<DoneButton onClick={handleDoneClick} disabled={!selectedClassId || missingLevelStudents.length > 0} />
							</div>
						</div>
						)}
					</div>
				)}
				</div>
				{(isGenerating || showReady) && (
					<LessonProgressModal
						state={showReady ? 'ready' : 'generating'}
						lessonName={(presentationFile?.name || '').replace(/\.[^/.]+$/, '')}
					/>
				)}
			</div>
		</>
	);
};

